import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { CheckCircle2, ChevronRight, MapPin, ArrowRight, BookOpen, TimerReset } from 'lucide-react';
import Seo, { breadcrumbSchema, faqSchema } from '@/components/Seo';
import { SectionHeading, FaqSection, EmergencyCta } from '@/components/sections';
import { SERVICES, ICON_MAP, BRAND } from '@/lib/site';
import { RESOURCES } from '@/lib/resources';

function serviceSchema(service) {
  const origin = (process.env.REACT_APP_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/$/, '');
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.title,
    description: service.seoDescription,
    url: `${origin}/services/${service.slug}`,
    serviceType: service.title,
    areaServed: [
      { '@type': 'City', name: 'Coquitlam', addressRegion: 'BC', addressCountry: 'CA' },
      { '@type': 'City', name: 'Port Coquitlam', addressRegion: 'BC', addressCountry: 'CA' },
      { '@type': 'City', name: 'Port Moody', addressRegion: 'BC', addressCountry: 'CA' },
    ],
    provider: {
      '@type': 'Organization',
      name: BRAND,
      url: origin || undefined,
    },
  };
}

export default function ServiceDetailPage({ slug }) {
  const service = SERVICES.find((item) => item.slug === slug);
  if (!service) return <Navigate to="/services" replace />;
  const Icon = ICON_MAP[service.icon];
  const relatedServices = service.related
    .map((relatedSlug) => SERVICES.find((item) => item.slug === relatedSlug))
    .filter(Boolean);

  return (
    <>
      <Seo
        title={`${service.seoTitle} | Tri-Cities Water Restoration`}
        description={service.seoDescription}
        image={service.heroImage}
        schema={[
          serviceSchema(service),
          faqSchema(service.faqs),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Services', path: '/services' },
            { name: service.title, path: `/services/${service.slug}` },
          ]),
        ]}
      />

      <section className="relative overflow-hidden bg-navy">
        <img
          src={service.heroImage}
          alt={service.heroAlt}
          className="absolute inset-0 h-full w-full object-cover opacity-50"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-deep/90 via-navy-deep/78 to-navy/30" />
        <div className="container-page relative py-16 lg:py-24">
          <nav aria-label="Breadcrumb" className="mb-6 text-sm text-white/60">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/services" className="hover:text-white">Services</Link>
            <span className="mx-2">/</span>
            <span className="text-white/90">{service.title}</span>
          </nav>

          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-water ring-1 ring-white/15">
              <Icon className="h-4 w-4" /> Service Guide
            </span>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl xl:text-6xl">
              {service.title}
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/80">{service.intro}</p>
            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/70">
              <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-water" /> Coquitlam</span>
              <span>Port Coquitlam</span>
              <span>Port Moody</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="container-page grid gap-10 lg:grid-cols-[.9fr_1.1fr] lg:items-start">
          <div>
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-water-light text-water-strong">
              <BookOpen className="h-6 w-6" />
            </span>
            <SectionHeading eyebrow="Understanding the Service" title={`What Is ${service.title}?`} />
          </div>
          <p className="text-base leading-8 text-muted-foreground sm:text-lg">{service.overview}</p>
        </div>
      </section>

      <section className="border-y border-border bg-secondary py-12 sm:py-16">
        <div className="container-page">
          <SectionHeading center eyebrow="How It Works" title="A Step-by-Step Service Process" sub="The exact response depends on the water source, materials involved and how far moisture has travelled." />
          <ol className="mx-auto mt-10 grid max-w-5xl gap-5 md:grid-cols-2 lg:grid-cols-5">
            {service.process.map((item, index) => (
              <li key={item} className="rounded-2xl border border-border bg-white p-5">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-navy-deep font-display text-sm font-bold text-white">{index + 1}</span>
                <p className="mt-4 text-sm leading-relaxed text-primary/85">{item}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="container-page grid gap-8 lg:grid-cols-2">
          <div className="rounded-3xl border border-border bg-white p-7 sm:p-9">
            <SectionHeading eyebrow="When It May Be Needed" title="Common Signs and Situations" />
            <ul className="mt-8 space-y-4">
              {service.signs.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-primary/85">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-water-strong" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl bg-navy-deep p-7 text-white sm:p-9">
            <span className="inline-block rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-water ring-1 ring-white/15">What to Expect</span>
            <h2 className="mt-4 max-w-xl font-display text-3xl font-extrabold leading-tight text-white sm:text-4xl">What Usually Happens During the Service</h2>
            <ul className="mt-8 space-y-4">
              {service.expect.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-white/80">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-water" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="border-y border-border bg-white py-12 sm:py-16">
        <div className="container-page grid gap-8 rounded-3xl bg-water-light/50 p-7 sm:p-10 lg:grid-cols-[auto_1fr] lg:items-start">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white text-water-strong shadow-sm">
            <TimerReset className="h-6 w-6" />
          </span>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-water-strong">Why Timing Matters</p>
            <h2 className="mt-2 font-display text-2xl font-extrabold text-primary sm:text-3xl">Why Earlier Action Can Reduce Further Damage</h2>
            <p className="mt-4 max-w-4xl text-base leading-8 text-muted-foreground">{service.whyFast}</p>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="container-page grid gap-8 lg:grid-cols-[.85fr_1.15fr] lg:items-center">
          <div>
            <SectionHeading
              eyebrow="Local Coverage"
              title="Available Across the Tri-Cities"
              sub="This service is available for homes and properties throughout our three-city service area."
            />
          </div>
          <div>
            <div className="grid gap-4 sm:grid-cols-3">
              {['Coquitlam', 'Port Coquitlam', 'Port Moody'].map((city) => (
                <div key={city} className="flex items-center gap-3 rounded-2xl border border-border bg-white p-5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-water-light text-water-strong">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <span className="font-display text-base font-bold text-primary">{city}</span>
                </div>
              ))}
            </div>
            <Link to="/areas" className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-water-strong hover:underline">
              See neighbourhoods in our service area <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-secondary py-12 sm:py-16">
        <div className="container-page">
          <SectionHeading eyebrow="Related Services" title="Other Water Damage Services You May Need" sub="Water damage often involves more than one stage. These related services explain what may happen before or after this step." />
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {relatedServices.map((related) => {
              const RelatedIcon = ICON_MAP[related.icon];
              return (
                <Link key={related.slug} to={`/services/${related.slug}`} className="group rounded-2xl border border-border bg-white p-6 transition hover:-translate-y-1 hover:border-water/40 hover:shadow-lg">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-water-light text-water-strong"><RelatedIcon className="h-5 w-5" /></span>
                  <h3 className="mt-4 font-display text-lg font-bold text-primary">{related.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{related.short}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-water-strong">Learn how it works <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-16"><div className="container-page"><SectionHeading eyebrow="Helpful Guides" title="Learn More About Water Damage" sub="Practical homeowner resources for situations connected with this service."/><div className="mt-8 grid gap-5 md:grid-cols-2">{RESOURCES.filter(a=>a.relatedServices.includes(service.slug)).slice(0,2).map(a=><Link key={a.slug} to={`/resources/${a.slug}`} className="group rounded-2xl border border-border bg-white p-6 transition hover:-translate-y-1 hover:border-water/40 hover:shadow-lg"><p className="text-xs font-semibold uppercase tracking-wider text-water-strong">Homeowner Guide</p><h3 className="mt-3 font-display text-xl font-bold text-primary">{a.title}</h3><p className="mt-2 text-sm leading-7 text-muted-foreground">{a.description}</p><span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-water-strong">Read guide <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1"/></span></Link>)}</div></div></section>

      <FaqSection
        faqs={service.faqs}
        eyebrow="Service FAQ"
        title={`Common Questions About ${service.title}`}
        sub="Answers to the questions homeowners commonly ask before or during this type of water-damage service."
        showHelpBox={false}
      />

      <EmergencyCta />
    </>
  );
}
